<template>
    <!-- markup and styles mirrored from src/views/Exam.vue (question stem, answers list, explanation - review mode) - keep visual changes in sync -->
    <div class="question-display">
        <div v-if="question.image_url" class="d-flex justify-center">
            <div class="mb-4 bordered btn-radius overflow-hidden pa-3">
                <v-img :transition="false" contain :src="question.image_url" max-height="300" max-width="600" rounded v-ripple="{class: 'ripple--color'}" @click="openFullImageViewer" class="e-link"></v-img>
            </div>
        </div>
        <div class="question-text strong-text--text mt-4 mb-8"
            :class="{
                'essay-question': question.type === 'essay',
                'short-question': question.text.length < 225,
                'long-question': question.text.length > 225,
                'rtl-dir text-right': questionDir === 'rtl',
                'ltr-dir text-left': questionDir === 'ltr',
                'md-and-up grey lighten-3': mdAndUp,
                'phone-only': phoneOnly
            }">
            {{question.text}}
        </div>

        <slot v-if="question.type === 'essay'" name="essay"></slot>
        <v-list v-else class="answers pa-0 my-4">
            <v-row>
                <v-col v-for="answer in question.answers" :key="answer.id" cols="12" :md="answersAreLong ? 12 : 6" class="py-0">
                    <v-list-item :key="answer.id" color="secondary" class="py-1 px-3 px-md-5 answer mb-2 lighten-5" :class="{'v-list-item--active': chosenAnswer === answer.id || (answer.correct && showDetailedResult), 'secondary--text': chosenAnswer === answer.id, 'error--text': answer.correct === false && showDetailedResult, 'success--text': answer.correct && showDetailedResult, 'ltr-dir text-left': detectDir(answer.text) === 'ltr', 'v-list-item--disabled': loading}" @click="isActive && !loading ? $emit('choose', answer) : ''" v-ripple="phoneOnly && isActive && !loading">
                        <v-list-item-icon :style="detectDir(answer.text) === 'ltr' ? 'direction: ltr; margin-left: 0; margin-right: 24px;' : ''">
                            <v-icon :color="showDetailedResult ? (answer.correct ? 'success' : (chosenAnswer === answer.id ? 'error' : 'muted')) : '' " style="border-radius: 50%" :style="chosenAnswer === answer.id ? 'box-shadow: 0 0 0 2px;' : ''">
                                {{answer.correct && showDetailedResult ? 'mdi-check-circle' : (answer.correct === false && showDetailedResult ? 'mdi-close-circle' : 'mdi-circle')}}{{(chosenAnswer === answer.id ? '' : '-outline')}}
                            </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title class="strong-text--text">
                                <div class="d-flex pa-2" v-if="answer.image_url">
                                    <img :src="answer.image_url" style="max-height: 75px; max-width: 100%"/>
                                </div>
                                <div class="answer-text strong-text--text" :class="{'font-weight-bold': question.type === 'truefalse'}" v-else>
                                    <template v-if="question.type === 'truefalse' && !showDetailedResult">
                                        <v-icon v-if="answer.text === 'صواب'" color="success darken-1" class="me-2">mdi-check-bold</v-icon>
                                        <v-icon v-else-if="answer.text === 'خطأ'" color="error" class="me-2">mdi-close-thick</v-icon>
                                    </template>
                                    {{answer.text}}
                                </div>
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-col>
            </v-row>
        </v-list>

        <template v-if="showDetailedResult && (question.answer_explain_text || question.answer_explain_image)">
            <v-divider class="my-8"></v-divider>
            <v-alert :color="answerCorrect ? 'indigo' : 'amber darken-1'" text outlined class="py-3 px-5">
                <div>
                    <h4 class="strong-text--text mb-1">توضيح من المعلم</h4>
                    <div v-if="question.answer_explain_text" class="medium-text strong-text--text" style="font-size: 0.95rem; white-space: pre-line;" v-html="urlify(escapeHtmlText(question.answer_explain_text))"></div>
                </div>
                <div v-if="question.answer_explain_image" style="position: relative;" class="mt-3">
                    <img :src="question.answer_explain_image" style="max-width: 100%; max-height: 420px; border-radius: 5px;" />
                </div>
            </v-alert>
        </template>

        <!-- full image viewer -->
        <img-viewer v-if="question.image_url" :src="question.image_url" @inited="photoViewerInited" />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import ImgViewer from '@/components/default/img-viewer'

import linkifyHtml from 'linkify-html';

export default {
    name: 'question-display',
    components: {
        ImgViewer
    },
    props: {
        question: {required: true},
        chosenAnswer: {default: null},
        showDetailedResult: {type: Boolean, default: false},
        isActive: {type: Boolean, default: false},
        loading: {type: Boolean, default: false},
        answerCorrect: {default: null},
    },
    data(){
        return {
            $viewer: null,
        }
    },
    computed: {
        ...GlobalComputed,
        questionDir(){
            return this.question && this.question.text ? this.detectDir(this.question.text) : 'rtl';
        },
        answersAreLong(){
            let long = false;
            if (this.question && this.question.answers){
                this.question.answers.forEach(answer=>{
                    if (answer.image_url || answer.text.length > 40){
                        long = true;
                    }
                });
            }
            return long;
        },
    },
    methods: {
        ...GlobalMethods,
        openFullImageViewer(){
            if (this.$viewer){
                this.$viewer.show();
            }
        },
        photoViewerInited(viewer) {
            this.$viewer = viewer;
        },
        detectDir(text){
            const rtlChar = /[\u0590-\u083F]|[\u08A0-\u08FF]|[\uFB1D-\uFDFF]|[\uFE70-\uFEFF]/mg;
            if (text){
                if (rtlChar.test(text.charAt(0))){
                    // is rtl
                    return 'rtl';
                }else{
                    return 'ltr';
                }
            }
            return 'rtl';
        },
        urlify(text){
            const options = { defaultProtocol: 'https' , target: '_system'};
            return linkifyHtml(text, options);
        },
        escapeHtmlText(text){
            // to prevent XSS attacks
            return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
        },
    }
}
</script>

<style lang="scss" scoped>
    // styles mirrored from src/views/Exam.vue (.question-card) - keep visual changes in sync
    .question-display{
        &, *{
            user-select: none!important;
        }
        .btn-radius{
            border-radius: 7px;
        }
        .answers{
            .v-list-item.answer{
                border-radius: 10px!important;
                overflow: hidden;
                border: 1px solid;
                &:not(.v-list-item--active){
                    border-color: rgba(0,0,0, .1);
                }
                &.theme--dark {
                    border-color: rgba(255,255,255, .1);
                }
            }
            .answer-text{
                white-space: pre-line;
            }
            &.theme--dark{
                background-color: transparent;
            }
        }
        .question-text{
            white-space: pre-line;
            &.short-question{
                font-size: 1rem;
                line-height: 1.75;

                &.md-and-up{
                    border-radius: 8px;
                    padding: 0.75rem 1.25rem;
                }
                &.phone-only{
                    font-size: 0.95rem;
                }

                &.essay-question{
                    font-weight: bold;
                }
            }
            &.long-question{
                font-size: 0.92rem;

                &.phone-only{
                    font-size: 0.88rem;
                }
            }
        }
    }
</style>
